import { useEffect, useRef } from "react";

interface UseBarcodeScannerOptions {
  onScan: (barcode: string) => void;
  /**
   * Tiempo máximo en ms entre caracteres para considerar que es un escaneo
   * Los escáneres envían caracteres muy rápido, típicamente < 50ms entre caracteres
   * @default 100
   */
  maxTimeBetweenChars?: number;
  /**
   * Si es true, solo escucha cuando no hay inputs de texto enfocados
   * @default true
   */
  ignoreWhenInputFocused?: boolean;
}

/**
 * Hook para escuchar escaneos de código de barras desde un escáner USB/HID.
 * Asume que todos los inputs de teclado provienen del escáner ya que la app usa touch.
 */
export function useBarcodeScanner({
  onScan,
  maxTimeBetweenChars = 100,
  ignoreWhenInputFocused = true,
}: UseBarcodeScannerOptions) {
  const bufferRef = useRef<string>("");
  const lastCharTimeRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Si hay un input enfocado y la opción está activada, ignorar
      if (
        ignoreWhenInputFocused &&
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.isContentEditable)
      ) {
        return;
      }

      const currentTime = Date.now();
      const timeSinceLastChar = currentTime - lastCharTimeRef.current;

      // Si pasó mucho tiempo desde el último carácter, resetear el buffer
      // (probablemente no es un escaneo continuo)
      if (timeSinceLastChar > maxTimeBetweenChars) {
        bufferRef.current = "";
      }

      // Capturar Enter o Return (final del código de barras)
      if (event.key === "Enter" || event.key === "Return") {
        event.preventDefault();
        event.stopPropagation();

        if (bufferRef.current.trim()) {
          const barcode = bufferRef.current.trim();
          bufferRef.current = "";
          onScan(barcode);
        }
        return;
      }

      // Capturar caracteres normales (excluyendo teclas especiales)
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        event.stopPropagation();

        bufferRef.current += event.key;
        lastCharTimeRef.current = currentTime;

        // Limpiar timeout anterior si existe
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Si no hay actividad por un tiempo, limpiar el buffer
        timeoutRef.current = setTimeout(() => {
          bufferRef.current = "";
        }, maxTimeBetweenChars * 2);
      }
    };

    // Agregar el listener al window para capturar todos los eventos
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onScan, maxTimeBetweenChars, ignoreWhenInputFocused]);
}
